/**
 * AlmanacMap.jsx — global Leaflet map of every geo-tagged
 * weather-almanac record. Uses raw Leaflet (via useEffect) instead of
 * react-leaflet to sidestep the StrictMode "Map container is already
 * initialized" double-mount bug.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  Catastrophic: "#7f1d1d",
  Historic:     "#b91c1c",
  Severe:       "#dc2626",
  Major:        "#ea580c",
  Moderate:     "#eab308",
  Minor:        "#65a30d",
};

// Canonical event-type list — mirrors the almanac LLM schema so the
// filter dropdown stays useful even when only a handful of records
// have been generated so far.
const CANONICAL_EVENT_TYPES = [
  "Heat Wave", "Cold Wave", "Blizzard", "Ice Storm", "Snowstorm",
  "Tropical Cyclone", "Hurricane", "Typhoon", "Tornado", "Tornado Outbreak",
  "Severe Thunderstorm", "Flash Flood", "River Flood", "Coastal Flood",
  "Atmospheric River", "Drought", "Dust Storm", "Sandstorm", "Monsoon",
  "Hailstorm", "Lightning Event", "Aurora", "Fog Event", "Wildfire Smoke",
  "Volcanic Ash", "Record Temperature", "Record Rainfall", "Record Snowfall",
  "Historic Storm", "Climate Milestone", "Rare Weather Event",
];

function severityColor(s) { return SEVERITY_COLORS[s] || "#0369a1"; }
function severityRadius(s) {
  return ({ Catastrophic: 12, Historic: 11, Severe: 10, Major: 9, Moderate: 8, Minor: 7 }[s]) || 8;
}
function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export default function AlmanacMap() {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState("");
  const [eventType, setEventType] = useState("");
  const [query, setQuery] = useState("");

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // Fetch pins once
  useEffect(() => {
    document.title = "Weather Almanac Map — Social Pulse";
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/weather-almanac/map?limit=3000`);
        const data = await res.json();
        setPins(Array.isArray(data.pins) ? data.pins : []);
      } catch {
        toast.error("Failed to load almanac map");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pins.filter((p) => {
      if (severity && p.severity !== severity) return false;
      if (eventType && p.event_type !== eventType) return false;
      if (q) {
        const hay = `${p.title || ""} ${p.city || ""} ${p.country || ""} ${p.event_type || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [pins, severity, eventType, query]);

  const uniqueEventTypes = useMemo(
    () => {
      const fromPins = new Set(pins.map((p) => p.event_type).filter(Boolean));
      // Merge canonical list with anything the LLM invented outside it.
      return [...new Set([...CANONICAL_EVENT_TYPES, ...fromPins])].sort();
    },
    [pins],
  );

  // Init the Leaflet map once. Cleanup guarantees the container is
  // freed so a StrictMode remount can safely re-init.
  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) return; // already initialised in this mount

    const map = L.map(containerRef.current, {
      center: [20, 0],
      zoom: 2,
      minZoom: 2,
      worldCopyJump: true,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      try { map.remove(); } catch {}
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Re-render pins whenever the filtered list changes.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const p of filtered) {
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: severityRadius(p.severity),
        color: severityColor(p.severity),
        fillColor: severityColor(p.severity),
        fillOpacity: 0.75,
        weight: 1.5,
      });
      const location = [p.city, p.country].filter(Boolean).join(", ") || "—";
      const badges = [
        p.event_type && `<span style="display:inline-block;padding:1px 6px;border:1px solid #cbd5e1;border-radius:999px;font-size:10px;margin-right:4px;">${escapeHtml(p.event_type)}</span>`,
        p.severity && `<span style="display:inline-block;padding:1px 6px;border-radius:999px;font-size:10px;margin-right:4px;color:#fff;background:${severityColor(p.severity)};">${escapeHtml(p.severity)}</span>`,
        (p.year || p.date) && `<span style="display:inline-block;padding:1px 6px;border:1px solid #cbd5e1;border-radius:999px;font-size:10px;">${escapeHtml(p.year || p.date)}</span>`,
      ].filter(Boolean).join("");
      const html = `
        <div style="font-size:13px;max-width:240px;">
          <div style="font-weight:600;color:#0f172a;">${escapeHtml(p.title || "Untitled record")}</div>
          <div style="color:#475569;margin-top:2px;">${escapeHtml(location)}</div>
          <div style="margin-top:6px;">${badges}</div>
          <a href="/almanac/records/${encodeURIComponent(p.slug || p.id)}"
             data-testid="almanac-map-open-${escapeHtml(p.slug || p.id)}"
             style="display:inline-block;margin-top:6px;color:#0369a1;font-weight:500;font-size:12px;text-decoration:none;">
            Open full record →
          </a>
        </div>`;
      marker.bindPopup(html);
      marker.bindTooltip(
        `<div style="font-size:11px;"><b>${escapeHtml(p.title || "")}</b><br/>${escapeHtml(location)}</div>`,
        { direction: "top", offset: [0, -6], opacity: 0.95 },
      );
      marker.addTo(layer);
    }
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="almanac-map-page">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="almanac-map-title">
            Weather Almanac — Global Map
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Every geo-tagged record in the almanac, plotted worldwide. Click a pin to open the full write-up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <Input
            placeholder="Search title, city, country…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="almanac-map-search"
          />
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            data-testid="almanac-map-severity"
          >
            <option value="">All severities</option>
            {Object.keys(SEVERITY_COLORS).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            data-testid="almanac-map-event-type"
          >
            <option value="">All event types</option>
            {uniqueEventTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="almanac-map-count">
              {filtered.length} / {pins.length} records
            </Badge>
            {(severity || eventType || query) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSeverity(""); setEventType(""); setQuery(""); }}
                data-testid="almanac-map-clear"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3" data-testid="almanac-map-legend">
          {Object.entries(SEVERITY_COLORS).map(([label, color]) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
              {label}
            </span>
          ))}
        </div>

        <div
          ref={containerRef}
          className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
          style={{ height: "70vh", width: "100%" }}
          data-testid="almanac-map-container"
        />

        {loading && (
          <p className="mt-3 text-sm text-slate-500" data-testid="almanac-map-loading">
            Loading pins…
          </p>
        )}
      </div>
    </div>
  );
}
